// These are all of the defined groups that NorthPoint Buildings & Projects and Comps can fall under.
// Each item should fall into exactly one group.
const labelledGroups = [
  {
    label: 'Industrial Buildings',
    collection: 'industrialBuildings',
    groups: [
      {name: 'Land Bank'},
      {name: 'Pipeline'},
      {name: 'Preconstruction'},
      {name: 'Construction'},
      {name: 'Complete'},
      {name: 'Sold'},
      {name: '3rd Party Managed'},
    ],
  },
  {
    label: 'Industrial Projects',
    collection: 'industrialProjects',
    groups: [
      {name: 'Pipeline', stages: ['Opportunity', 'Initial Analysis', 'LOI']},
      {name: 'Advanced Analysis', stages: ['Advanced Analysis']},
      {name: 'Under Contract', stages: ['Under Contract']},
      {name: 'Closed', stages: ['Closed']},
      {name: 'Under Construction', stages: ['Under Construction']},
      {name: 'Completed', stages: ['Completed']},
      {name: 'Sold', stages: ['Sold']},
      {name: 'Dropped', stages: []},
      {name: '3rd Party Managed', stages: ['3rd Party Managed']},
    ],
  },
  {
    label: 'Multi-Family Projects',
    collection: 'multiFamilyProjects',
    groups: [
      {name: 'Pipeline', stages: ['Opportunity', 'LOI']},
      {name: 'Under Contract', stages: ['Under Contract']},
      {name: 'Closed', stages: ['Closed']},
      {name: 'Under Construction', stages: ['Under Construction']},
      {name: 'Completed', stages: ['Completed']},
      {name: 'Sold', stages: ['Sold']},
      {name: 'Dropped', stages: []},
      {name: '3rd Party Managed', stages: ['3rd Party Managed']},
    ],
  },
  {
    label: 'Acquisition Projects',
    collection: 'acquisitionProjects',
    groups: [
      {name: 'Opportunity', stages: ['Opportunity']},
      {name: 'Initial Analysis', stages: ['Initial Analysis']},
      {name: 'Advanced Analysis', stages: ['Advanced Analysis']},
      {name: 'LOI', stages: ['LOI']},
      {name: 'Best & Final', stages: ['Best & Final']},
      {name: 'Due Diligence', stages: ['Due Diligence']},
      {name: 'Under Contract', stages: ['Under Contract']},
      {name: 'Closed', stages: ['Closed']},
      {name: 'Sold', stages: ['Sold']},
      {name: 'Dropped', stages: []},
      {name: '3rd Party Managed', stages: ['3rd Party Managed']},
    ],
  },
  {
    label: 'Comps',
    collection: 'competitiveBuildingComps',
    groups: [
      {name: 'Lease'},
      {name: 'Land Sale'},
      {name: 'Building Sale'},
    ],
  },
];

function getGroupsByCollection(c) {
  return labelledGroups.find(({collection}) => c === collection).groups;
}

/**
 * Determines the filtering group a NorthPoint Building falls into.
 * @param {object} building The building object.
 * @return {string} The name of the group.
 */
function getGroupForBuilding(building) {
  const groups = getGroupsByCollection('industrialBuildings');
  const displayFields = building.displayFields;
  if (!displayFields) {
    throw new Error('No display fields found on building object');
  }
  const stage = displayFields.find(({label}) => label === 'Building Stage')?.value;
  // Find the first group where the name equals the stage from the metadata
  const group = groups.find(({name}) => name === stage);
  // If the group exists and has a name, return that. Else, return unknown
  return group?.name ?? 'Unknown';
}

/**
 * Determines the filtering group an Industrial Project falls into.
 * @param {object} project The project object.
 * @return {string} The name of the group.
 */
function getGroupForIndustrialProject(project) {
  const status = project.projectStatus;
  const stage = project.displayFields.find(({label}) => label === 'Project Stage')?.value;
  if (status === 'Inactive' && stage !== 'Sold') return 'Dropped';
  return getGroupsByCollection('industrialProjects')
    .find(({stages}) => stages.includes(stage))?.name ?? 'Unknown';
}

/**
 * Determines the filtering group a Multi-Family Project falls into.
 * @param {object} project The project object.
 * @return {string} The name of the group.
 */
function getGroupForMultiFamilyProject(project) {
  const status = project.projectStatus;
  const stage = project.displayFields.find(({label}) => label === 'Project Stage')?.value;
  if (status === 'Inactive' && stage !== 'Sold') return 'Dropped';
  return getGroupsByCollection('multiFamilyProjects')
    .find(({stages}) => stages.includes(stage))?.name ?? 'Unknown';
}

/**
 * Determines the filtering group an Acquisition Project falls into.
 * @param {object} project The project object.
 * @return {string} The name of the group.
 */
function getGroupForAcquisitionProject(project) {
  const status = project.projectStatus;
  const stage = project.displayFields.find(({label}) => label === 'Project Stage')?.value;
  if (status === 'Inactive' && stage !== 'Sold') return 'Dropped';
  return getGroupsByCollection('acquisitionProjects')
    .find(({stages}) => stages.includes(stage))?.name ?? 'Unknown';
}

/**
 * Determines the filtering group a comp falls into.
 * @param {object} comp The comp object.
 * @return {string} The name of the group.
 */
function getGroupForComp(comp) {
  return getGroupsByCollection('competitiveBuildingComps')
    .find(({name}) => name === comp.type)?.name ?? 'Unknown';
}

export {
  getGroupsByCollection,
  getGroupForBuilding,
  getGroupForIndustrialProject,
  getGroupForMultiFamilyProject,
  getGroupForAcquisitionProject,
  getGroupForComp,
};
